import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Strikethrough,
  Underline,
} from "lucide-react";
import {
  rememberLabelStyle,
  useCanvasLabelStyle,
  type CanvasLabelAlign,
} from "../../../../hooks/useCanvasLabelStyle";
import { LABEL_COLORS } from "../../../../nodes/shared/CanvasColorPalettes";
import { markOf } from "../../../../nodes/shared/canvasColorRoles";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_TILE_GLYPH,
} from "../../canvasToolbarComposition";
import { CANVAS_FONT_SIZE_PRESETS } from "../../CanvasToolbarFontSizeMenu";
import { CANVAS_TYPEFACES } from "../../CanvasToolbarTypefaceMenu";
import {
  SwatchGrid,
  TileGrid,
  Tray,
  TrayDeck,
  TrayNewRow,
  TrayPair,
  TraySection,
  type TrayNewRowProps,
} from "../CanvasTrayContent";

const glyph = (Icon: typeof Bold) => (
  <Icon
    size={CANVAS_TILE_GLYPH}
    strokeWidth={CANVAS_GLYPH_STROKE}
    aria-hidden="true"
  />
);

const STYLES = [
  { key: "is_bold", name: "Bold", node: glyph(Bold) },
  { key: "is_italic", name: "Italic", node: glyph(Italic) },
  { key: "is_underline", name: "Underline", node: glyph(Underline) },
  {
    key: "is_strikethrough",
    name: "Strikethrough",
    node: glyph(Strikethrough),
  },
] as const;

const ALIGNS: { key: CanvasLabelAlign; name: string; node: React.ReactNode }[] =
  [
    { key: "left", name: "Align left", node: glyph(AlignLeft) },
    { key: "center", name: "Align center", node: glyph(AlignCenter) },
    { key: "right", name: "Align right", node: glyph(AlignRight) },
  ];

const SIZE_STEPS: Record<string, string> = {
  "12px": "text-sm",
  "16px": "text-base",
  "22px": "text-md",
  "32px": "text-lg",
  "48px": "text-xl",
};

/**
 * Text · Label sets the DEFAULT the next label is placed in — the style
 * `useCanvasLabelStyle` remembers; the contextual bar edits an instance.
 * Bottom-up: alignment (set once) at the top, then the face and its span
 * operations, the colour, the size ladder, and — popped only, since the
 * tile arms placement as it opens the docked tray — New label nearest the
 * bar, which arms placement the way a shape cell does. The typeface cells
 * render the word in the face itself.
 */
export function TextTray({
  newLabel,
}: {
  /** The New label row; absent, the row is not drawn. */
  newLabel?: TrayNewRowProps;
}) {
  const style = useCanvasLabelStyle();

  const faces = CANVAS_TYPEFACES.map((f) => ({
    key: f.value,
    name: f.name,
    node: (
      <span
        className="block text-md leading-none"
        style={{ fontFamily: f.value }}
        aria-hidden="true"
      >
        Ag
      </span>
    ),
  }));
  const sizes = CANVAS_FONT_SIZE_PRESETS.map((s) => ({
    key: s.value,
    name: s.name,
    node: (
      <span
        className={`block font-sans leading-none font-medium ${SIZE_STEPS[s.value] ?? "text-base"}`}
        aria-hidden="true"
      >
        A
      </span>
    ),
  }));
  const colours = LABEL_COLORS.map((c) => ({
    key: c.value,
    name: c.name,
    mark: c.value === "default" ? c.swatchHex : c.value,
  }));
  const styles = new Set(
    STYLES.filter((s) => style[s.key]).map((s) => s.key as string),
  );

  return (
    <Tray>
      <TrayDeck>
        <TraySection ariaLabel="Alignment">
          <TileGrid
            items={ALIGNS}
            value={style.align}
            onChange={(k) =>
              rememberLabelStyle({ align: k as CanvasLabelAlign })
            }
            columns={ALIGNS.length}
          />
        </TraySection>
        <TrayPair>
          <TraySection ariaLabel="Typeface">
            <TileGrid
              items={faces}
              value={style.font_family}
              onChange={(font_family) => rememberLabelStyle({ font_family })}
              columns={faces.length}
            />
          </TraySection>
          <TraySection ariaLabel="Style">
            <TileGrid
              items={[...STYLES]}
              values={styles}
              onToggle={(k) =>
                rememberLabelStyle({
                  [k]: !style[k as (typeof STYLES)[number]["key"]],
                })
              }
              columns={STYLES.length}
            />
          </TraySection>
        </TrayPair>
        <TraySection ariaLabel="Color">
          <SwatchGrid
            role="text"
            items={colours}
            value={markOf(style.text_color ?? "default")}
            onChange={(k) =>
              rememberLabelStyle({ text_color: k === "default" ? null : k })
            }
            columns={colours.length}
          />
        </TraySection>
        <TraySection ariaLabel="Size">
          <TileGrid
            items={sizes}
            value={style.font_size}
            onChange={(font_size) => rememberLabelStyle({ font_size })}
            columns={sizes.length}
          />
        </TraySection>
        {newLabel ? <TrayNewRow label="New label" {...newLabel} /> : null}
      </TrayDeck>
    </Tray>
  );
}
